import React, { Suspense, lazy, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box } from "@familyzone/component-library";

import { useLicenseStore } from "stores/v2/licenseStore";
import Nav from "../Nav";
import { TabBar } from "../TabBar";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";
import { useClassroomDetailsQuery } from "js/query/useClassroomDetailsQuery";
import _ from "lodash";
import shallow from "zustand/shallow";
import useStore from "stores/index";
import { useMount } from "react-use";
import { getCoursesMicrofrontendUrl } from "js/utils/getMicrofrontendUrl";
import LoadingFallback from "./LoadingFallback";
import ErrorFallback from "./ErrorFallback";
import { useThemeStore } from "stores/v2/themeStore";
import { CLASSROOM_TYPES } from "types/classrooms";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import ErrorBoundary from "../ErrorBoundary";
import ModuleLoadingFallback from "../ModuleLoadingFallback";
import { loadDynamicRemote } from "@/utils/loadDynamicRemote";

const CoursesWindow = () => {
	const params = useParams();
	const licenseStore = useLicenseStore();
	const themeStore = useThemeStore();

	const classroomStore = useStore(
		(state) => ({
			getClassroomV2: state.classroom.getClassroomV2,
			fetchClassrooms: state.classroom.fetchClassrooms,
		}),
		shallow
	);

	const selectedClassroomStore = useSelectedClassroomStore(
		(state) => ({
			setData: state.setData,
		}),
		shallow
	);

	// purposefully only contains a small amount of the classroom info, use classroomQuery.data for full object. EDU-4354 should remove the requirement for this state.
	const [classroom, setClassroom] = useState(
		_.pick(classroomStore.getClassroomV2(params.classId), ["id", "name", "type", "sourceId", "personalisation", "description"])
	);

	const classroomQuery = useClassroomDetailsQuery(
		params.classId,
		true,
		true,
		true,
		false,
		classroom?.type,
		classroom?.sourceId,
		useUserSessionStore.getState().getTeacherId(),
		{
			enabled: classroom && !_.isEmpty(classroom),
			select: (data) => data.classroom,
		}
	);

	// actions to run when classroomQuery data is available/updated
	useEffect(() => {
		if (!classroomQuery.data || !classroom.personalisation) {
			return;
		}
		setClassroom({ ...classroomQuery.data, personalisation: classroom.personalisation });
	}, [classroomQuery.data, classroom.personalisation]);

	const isClassroomDataAvailable = !_.isEmpty(classroomStore.getClassroomV2(params.classId));

	// fetch classrooms if it isn't already fetched - this occurs when the active classroom page is refreshed or visited directly, rather then navigating from classrooms overview
	useMount(async () => {
		if (!isClassroomDataAvailable) {
			await classroomStore.fetchClassrooms();
			const _classroom = classroomStore.getClassroomV2(params.classId);
			setClassroom(_classroom);
			selectedClassroomStore.setData(_classroom);
		}
	});

	const classroomType = classroom.type.toLowerCase() === CLASSROOM_TYPES.CLASSROOM ? "CLASS" : "GROUP";
	const coursesModuleRemoteUrl = getCoursesMicrofrontendUrl(window.location.hostname) + "?nocache=" + new Date().getTime();

	const DynamicRemoteApp = lazy(() => {
		return loadDynamicRemote(coursesModuleRemoteUrl, "CoursesModule", "./app", "webpack", "var");
	});

	return (
		<Box>
			<Nav selectedClass={classroom} />
			<TabBar />
			{!!classroomQuery.data && (
				<ErrorBoundary fallback={<ErrorFallback />}>
					<Suspense fallback={<LoadingFallback /> ?? <ModuleLoadingFallback />}>
						<DynamicRemoteApp
							classId={params.classId}
							classType={classroomType}
							isLicensed={licenseStore.courses}
							classwizeTheme={themeStore.theme}
							applianceId={localStorage.getItem("DEVICE")}
							token={localStorage.getItem("TOKEN")}
						/>
					</Suspense>
				</ErrorBoundary>
			)}
		</Box>
	);
};

export default CoursesWindow;
